import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Share2, Shield, CheckCircle2, X } from 'lucide-react';

interface AIShareControlsProps {
  petName: string;
  onClose: () => void;
}

export type ShareLevel = 'full' | 'summary' | 'zkp-only' | 'none';

const SHARE_LEVEL_KEYS: Record<ShareLevel, { titleKey: string; descKey: string }> = {
  full: { titleKey: 'fullInsights', descKey: 'fullInsightsDesc' },
  summary: { titleKey: 'summaryOnly', descKey: 'summaryOnlyDesc' },
  'zkp-only': { titleKey: 'zkpFlagsOnly', descKey: 'zkpFlagsOnlyDesc' },
  none: { titleKey: 'nothingPrivate', descKey: 'nothingPrivateDesc' }
};

export function AIShareControls({ petName, onClose }: AIShareControlsProps) {
  const { t } = useTranslation();
  const [shareLevel, setShareLevel] = useState<ShareLevel>('full');
  const [autoShareWithVets, setAutoShareWithVets] = useState(true);
  const [autoShareWithGroomers, setAutoShareWithGroomers] = useState(false);

  const shareLevels: Array<{
    id: ShareLevel;
    titleKey: string;
    descKey: string;
    icon: typeof Lock;
  }> = [
    { id: 'full', titleKey: 'fullInsights', descKey: 'fullInsightsDesc', icon: Share2 },
    { id: 'summary', titleKey: 'summaryOnly', descKey: 'summaryOnlyDesc', icon: Shield },
    { id: 'zkp-only', titleKey: 'zkpFlagsOnly', descKey: 'zkpFlagsOnlyDesc', icon: Lock },
    { id: 'none', titleKey: 'nothingPrivate', descKey: 'nothingPrivateDesc', icon: Lock }
  ];

  const handleSave = () => {
    // In real implementation, this would save to the database
    console.log('Share settings saved:', { shareLevel, autoShareWithVets, autoShareWithGroomers });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border dark:border-white/8">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 dark:bg-white/10 rounded-xl flex items-center justify-center">
                <Lock className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>{t('features.aiInsights.shareSettingsTitle')}</h2>
                <p className="text-blue-100 dark:text-blue-200 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.aiInsights.shareSettingsForPet', { petName })}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 dark:bg-white/10 dark:hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 bg-white dark:bg-[#16151D]">
          {/* Info Banner */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-900 dark:text-blue-200" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('features.aiInsights.shareSettingsInfoBanner')}
            </p>
          </div>

          {/* Share Level Selection */}
          <div className="space-y-3">
            <h3 className="text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>{t('features.aiInsights.whatToShare')}</h3>
            <div className="space-y-2">
              {shareLevels.map(level => {
                const Icon = level.icon;
                const isSelected = shareLevel === level.id;
                
                return (
                  <button
                    key={level.id}
                    onClick={() => setShareLevel(level.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-white/20 bg-white dark:bg-[#0F0E16] hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-white/10'
                      }`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-white/70'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className={`${isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'}`} style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
                            {t(`features.aiInsights.${level.titleKey}`)}
                          </h4>
                          {isSelected && (
                            <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>{t(`features.aiInsights.${level.descKey}`)}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Auto-Share Settings */}
          {shareLevel !== 'none' && (
            <div className="space-y-3">
              <h3 className="text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>{t('features.aiInsights.autoShareWith')}</h3>
              <p className="text-sm text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                {t('features.aiInsights.autoShareWithDesc')}
              </p>
              
              <div className="space-y-2">
                <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg border border-gray-200 dark:border-white/8 cursor-pointer hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                  <div className="flex-1">
                    <h4 className="text-gray-900 dark:text-white mb-1" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>{t('features.aiInsights.veterinarians')}</h4>
                    <p className="text-sm text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {t('features.aiInsights.veterinariansDesc')}
                    </p>
                  </div>
                  <div className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={autoShareWithVets}
                      onChange={(e) => setAutoShareWithVets(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 dark:bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-white/20 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500"></div>
                  </div>
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg border border-gray-200 dark:border-white/8 cursor-pointer hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                  <div className="flex-1">
                    <h4 className="text-gray-900 dark:text-white mb-1" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>{t('features.aiInsights.groomersAndTrainers')}</h4>
                    <p className="text-sm text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {t('features.aiInsights.groomersAndTrainersDesc')}
                    </p>
                  </div>
                  <div className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={autoShareWithGroomers}
                      onChange={(e) => setAutoShareWithGroomers(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 dark:bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-white/20 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500"></div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-500/30 rounded-lg">
            <h4 className="text-purple-900 dark:text-purple-100 mb-2" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>{t('features.aiInsights.currentConfiguration')}</h4>
            <div className="space-y-1 text-sm text-purple-800 dark:text-purple-200" style={{ fontFamily: 'Inter, sans-serif' }}>
              <p>• {t('features.aiInsights.sharingLevel')}: <span className="font-medium">{t(`features.aiInsights.${SHARE_LEVEL_KEYS[shareLevel].titleKey}`)}</span></p>
              {shareLevel !== 'none' && (
                <>
                  <p>• {t('features.aiInsights.autoShareVets')}: <span className="font-medium">{autoShareWithVets ? t('features.aiInsights.yes') : t('features.aiInsights.no')}</span></p>
                  <p>• {t('features.aiInsights.autoShareGroomers')}: <span className="font-medium">{autoShareWithGroomers ? t('features.aiInsights.yes') : t('features.aiInsights.no')}</span></p>
                </>
              )}
            </div>
          </div>

          {/* Privacy Note */}
          <div className="p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg border border-gray-200 dark:border-white/8">
            <div className="flex items-start gap-2">
              <Shield className="w-5 h-5 text-gray-600 dark:text-white/70 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700 dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                <p className="mb-2">
                  <span className="font-medium">{t('features.aiInsights.privacyProtection')}: </span>
                  {t('features.aiInsights.privacyNote1')}
                </p>
                <p>
                  {t('features.aiInsights.privacyNote2')}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 dark:hover:from-blue-600 dark:hover:to-purple-600 transition-all"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('features.aiInsights.saveSettings')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
